import React, { memo, ReactNode, useId } from 'react';
import type { BoxProps, Theme } from '@mui/material';
import { Box, Stack, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

export interface Props {
  label: ReactNode;
  value: ReactNode;
  orientation?: 'horizontal' | 'vertical';
  id?: string;
  sx?: SystemStyleObject<Theme>;
  className?: string;
  spacing?: number | string;
  valueProps?: BoxProps;
}

export const LabelValue = memo(function LabelValue({
  label,
  value,
  orientation = 'horizontal',
  id,
  sx = {},
  className,
  spacing = 0,
  valueProps = {}
}: Props): JSX.Element {
  const reactId = useId();
  const baseId = id ?? `label-value-${reactId}`;
  const labelId = `${baseId}-label`;
  const valueId = `${baseId}-value`;

  const isHorizontal = orientation === 'horizontal';

  return (
    <Box
      component="dl"
      className={className}
      sx={{
        m: 0,
        p: 0,
        ...sx,
      }}
    >
      <Stack
        direction={isHorizontal ? 'row' : 'column'}
        spacing={spacing}
        alignItems={isHorizontal ? 'center' : 'flex-start'}
        sx={{
          flexWrap: isHorizontal ? { xs: 'wrap', sm: 'nowrap' } : 'nowrap',
          '& dt': { m: 0 },
          '& dd': { m: 0 },
        }}
      >
        <Box component="dt" id={labelId} sx={{ display: 'inline-block' }}>
          <Typography
            variant="body2"
            color="textSecondary"
            component="span"
            sx={{ fontWeight: 600, lineHeight: 1.2 }}
          >
            {label}
          </Typography>
        </Box>

        <Box
          component="dd"
          id={valueId}
          aria-labelledby={labelId}
          sx={{
            display: 'inline-block',
            flex: isHorizontal ? 1 : 'initial',
            '& > *': { minWidth: 0 },
            ...(valueProps.sx ?? {}),
          }}
          {...valueProps}
        >
          <Typography variant="body1" component="span" sx={{ lineHeight: 1.4 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
});
